/**
 * @file IntegrationSecretFields.tsx
 * @description Поля ключей доступа и настроек интеграции для диалога
 * `IntegrationEditDialog` (phase19.29: вынесены из диалога, чтобы он
 * уложился в 200 строк).
 *
 * Состав полей — из реестра `INTEGRATION_KIND_META[kind]` (credentialFields /
 * configFields): новый вид интеграции = запись в реестре, разметка не
 * меняется. Контракт `value/onChange` — как у `CarrierMainFields`.
 */

import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import type { IntegrationKindMeta } from "../../lib/api-platform-integrations";

interface CredentialProps {
  meta: IntegrationKindMeta;
  /** Редактирование: пустое поле = «не менять». */
  editing: boolean;
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}

/** Ключи доступа (хранятся на сервере зашифрованными). */
export function IntegrationCredentialFields({ meta, editing, value, onChange }: CredentialProps) {
  const set = (key: string, v: string) => onChange({ ...value, [key]: v });
  return (
    <>
      <div className="label-editorial pt-2">
        Ключи доступа {editing ? "(оставьте пустыми, чтобы не менять)" : ""}
      </div>
      {meta.credentialFields.map((field) => (
        <div key={field.key} className="flex flex-col gap-1.5">
          <Label htmlFor={`int-cred-${field.key}`}>
            {field.label}
            {field.required ? " *" : ""}
          </Label>
          {field.type === "textarea" ? (
            <Textarea
              id={`int-cred-${field.key}`}
              value={value[field.key] ?? ""}
              onChange={(e) => set(field.key, e.target.value)}
              rows={4}
            />
          ) : (
            <Input
              id={`int-cred-${field.key}`}
              type={field.type === "password" ? "password" : "text"}
              value={value[field.key] ?? ""}
              onChange={(e) => set(field.key, e.target.value)}
              autoComplete="off"
            />
          )}
          {field.helperText ? (
            <p className="text-xs text-on-surface-variant">{field.helperText}</p>
          ) : null}
        </div>
      ))}
    </>
  );
}

interface ConfigProps {
  meta: IntegrationKindMeta;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}

/** Несекретные настройки вида интеграции; нет полей — блока нет. */
export function IntegrationConfigFields({ meta, value, onChange }: ConfigProps) {
  if (!meta.configFields || meta.configFields.length === 0) return null;
  const set = (key: string, v: unknown) => onChange({ ...value, [key]: v });
  return (
    <>
      <div className="label-editorial pt-2">Настройки</div>
      {meta.configFields.map((field) =>
        field.type === "checkbox" ? (
          <label key={field.key} className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={Boolean(value[field.key])}
              onCheckedChange={(checked) => set(field.key, checked === true)}
            />
            <span className="text-sm text-on-surface">{field.label}</span>
          </label>
        ) : (
          <div key={field.key} className="flex flex-col gap-1.5">
            <Label htmlFor={`int-cfg-${field.key}`}>{field.label}</Label>
            <Input
              id={`int-cfg-${field.key}`}
              type={field.type === "number" ? "number" : "text"}
              value={String(value[field.key] ?? "")}
              onChange={(e) =>
                set(field.key, field.type === "number" ? Number(e.target.value) : e.target.value)
              }
            />
            {field.helperText ? (
              <p className="text-xs text-on-surface-variant">{field.helperText}</p>
            ) : null}
          </div>
        ),
      )}
    </>
  );
}
